import { useQuery } from '@tanstack/react-query'
import { PromoCodeItem, SearchPromoCodeApiParams, SearchPromoCodeParams } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { PROMO_CODE_STATUS_TO_CODE, PROMO_CODE_TYPE_TO_API } from 'constants/promo-code'
import { mapApiToPromoCodeItem } from 'modules/promo-code/api-adapters'
import { logError } from 'utils/logger'

import { promoCodeKeys } from './query-keys'

interface PromoCodeListResult {
  results: PromoCodeItem[]
  total: number
}

// FE sort tokens (driven by table column dataIndex) → BE sort fields.
const SORT_FIELD_TO_API: Record<string, string> = {
  createdAt: 'createdAt',
  startDate: 'startAt',
  endDate: 'endAt',
  usedTimes: 'usedTimes',
  totalPtosApplied: 'totalPtosApplied',
}

const toApiSort = (sort?: string): string | undefined => {
  if (!sort) return undefined
  const [field, dir] = sort.split(':')
  return `${SORT_FIELD_TO_API[field] ?? field}:${dir}`
}

const fetchPromoCodes = async (params: SearchPromoCodeParams): Promise<PromoCodeListResult> => {
  const apiParams: SearchPromoCodeApiParams = {
    page: params.page,
    limit: params.limit,
    code: params.keyword,
    status: params.status ? PROMO_CODE_STATUS_TO_CODE[params.status] : undefined,
    typeBonus: params.type ? PROMO_CODE_TYPE_TO_API[params.type] : undefined,
    sort: toApiSort(params.sort),
  }

  try {
    const res = await AppAdmin.getPromoCodes(apiParams)
    const payload = res.data?.data
    const results = (payload?.results ?? []).map(mapApiToPromoCodeItem)
    return {
      results,
      total: payload?.pagination?.totalResults ?? results.length,
    }
  } catch (error) {
    logError('Error fetching promo codes:', error)
    return { results: [], total: 0 }
  }
}

export const usePromoCodes = (params: SearchPromoCodeParams) => {
  const query = useQuery({
    queryKey: promoCodeKeys.list(params),
    queryFn: () => fetchPromoCodes(params),
    gcTime: 30000,
  })

  return {
    promoCodes: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  }
}
